import AlarmOffRoundedIcon from '@mui/icons-material/AlarmOffRounded'
import AlarmRoundedIcon from '@mui/icons-material/AlarmRounded'
import CloseIcon from '@mui/icons-material/Close'
import SearchIcon from '@mui/icons-material/Search'

import LocalStorageButtons from '@components/LocalStorageButtons'
import { FilterAutosuggest } from '@components/toolbar/FilterAutosuggest'
import { FilterDatePicker } from '@components/toolbar/FilterDatePicker'
import FilterGridContainer from '@components/toolbar/FilterGridContainer'
import FilterGridItem from '@components/toolbar/FilterGridItem'
import { FilterSelectObject } from '@components/toolbar/FilterSelectObject'
import { FilterSelectString } from '@components/toolbar/FilterSelectString'
import { FilterToggleButton } from '@components/toolbar/FilterToggleButton'
import type { FilterOption } from '@models/Filter'

import { useLocalFilters } from '../../../core/hooks/useLocalFilters'
import { StyledIconButton } from '../../../theme/components/StyledIconButton'
import { queryKey } from '../hooks/useHoofdnetOpdrachtenSearch'
import type { HoofdnetOpdrachtenSearchFilters as Filters } from '../types/HoofdnetOpdrachtenSearch'

interface Props {
  onChange: (filters: Filters, removeSort?: boolean) => void
  onRefresh: () => void
  onReset: () => void
  filters: Filters
  initialFilters: Filters
  onSearchChange: any
  options: any
  filterOptions: {
    opdrachtgevers: string[]
    opdrachtnemers: string[]
    types: string[]
    disciplines: FilterOption[]
    versies: string[]
  }
}

export const HoofdnetOpdrachtenFilters = ({
  onChange,
  onRefresh,
  onReset,
  filters,
  initialFilters,
  onSearchChange,
  options,
  filterOptions: {
    opdrachtgevers,
    opdrachtnemers,
    types,
    disciplines,
    versies,
  },
}: Props) => {
  const { localFilters, handleFilterChange, handleZoeken, handleReset } =
    useLocalFilters<Filters>({
      filters,
      initialFilters,
      onChange,
      onRefresh,
      onReset,
    })

  const {
    type,
    opdrachtnemer,
    opdrachtgever,
    discipline,
    search,
    open,
    versieNummer,
    creatiedatum,
    changed,
  } = localFilters

  return (
    <FilterGridContainer>
      <FilterGridItem>
        <FilterSelectString
          options={types}
          label="Type"
          filterKey="type"
          value={type}
          onFilterChange={handleFilterChange}
        />
      </FilterGridItem>

      <FilterGridItem>
        <FilterSelectString
          options={versies}
          label="Versienummer"
          filterKey="versieNummer"
          value={versieNummer}
          onFilterChange={handleFilterChange}
        />
      </FilterGridItem>

      <FilterGridItem>
        <FilterSelectObject
          options={disciplines}
          label="Discipline"
          filterKey="discipline"
          value={discipline}
          onFilterChange={handleFilterChange}
        />
      </FilterGridItem>

      <FilterGridItem>
        <FilterDatePicker
          label="Creatiedatum"
          filterKey="creatiedatum"
          value={creatiedatum}
          onFilterChange={handleFilterChange}
        />
      </FilterGridItem>

      <FilterGridItem>
        <FilterDatePicker
          label="Mutatiedatum"
          filterKey="changed"
          value={changed}
          onFilterChange={handleFilterChange}
        />
      </FilterGridItem>

      {/* If there is only 1 netbeheerder, hide the list. It's unnecessary to display it */}
      {opdrachtgevers.length > 1 && (
        <FilterGridItem>
          <FilterSelectString
            options={opdrachtgevers}
            label="Opdrachtgever"
            filterKey="opdrachtgever"
            value={opdrachtgever}
            onFilterChange={handleFilterChange}
          />
        </FilterGridItem>
      )}

      {opdrachtnemers.length > 1 && (
        <FilterGridItem>
          <FilterSelectString
            options={opdrachtnemers}
            label="Opdrachtnemer"
            filterKey="opdrachtnemer"
            value={opdrachtnemer}
            onFilterChange={handleFilterChange}
          />
        </FilterGridItem>
      )}

      <FilterGridItem>
        <FilterAutosuggest
          search={search}
          options={options}
          onFilterChange={handleFilterChange}
          onSearchChange={onSearchChange}
        />
      </FilterGridItem>

      <FilterGridItem
        minColumns="full"
        sx={{
          display: 'flex',
          justifyContent: 'flex-end',
        }}
      >
        <StyledIconButton
          icon={<CloseIcon />}
          title="Reset filters"
          onClick={handleReset}
        />
        <StyledIconButton
          icon={<SearchIcon />}
          title="Filters toepassen"
          onClick={handleZoeken}
        />

        <FilterToggleButton
          filterKey="open"
          value={open}
          onFilterChange={handleFilterChange}
          activeIcon={<AlarmRoundedIcon />}
          inactiveIcon={<AlarmOffRoundedIcon />}
          activeTitle="Klik om ook afgesloten opdrachten te tonen"
          inactiveTitle="Klik om alleen open opdrachten te tonen"
        />

        <LocalStorageButtons
          name={queryKey}
          storable={localFilters}
          onClickLoad={handleFilterChange}
        />
      </FilterGridItem>
    </FilterGridContainer>
  )
}
